import { useState, useMemo, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { X, ChevronsUpDown, Search, Filter, Microscope, Check } from 'lucide-react';



// Group icons and colors
const GROUP_CONFIG = {
  'Kiselalger': { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: '🔬' },
  'Fureflagellater': { color: 'bg-red-100 text-red-800 border-red-200', icon: '🔴' },
  'Svepeflagellater': { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: '🟠' },
  'Kalkflagellater': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: '🔵' },
  'Nåleflagellater': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: '🟣' },
  'Grønnalger': { color: 'bg-green-100 text-green-800 border-green-200', icon: '🟢' },
  'Ciliater': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '🟡' },
  'Hoppekreps': { color: 'bg-cyan-100 text-cyan-800 border-cyan-200', icon: '🦐' },
  'Krill': { color: 'bg-pink-100 text-pink-800 border-pink-200', icon: '🦞' },
  'Larver fisk': { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: '🐟' },
  'Larver skjell': { color: 'bg-slate-100 text-slate-800 border-slate-200', icon: '🐚' },
  'Manet': { color: 'bg-violet-100 text-violet-800 border-violet-200', icon: '🪼' },
  'Pilorm': { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: '🪱' },
  'Ribbemanet': { color: 'bg-rose-100 text-rose-800 border-rose-200', icon: '💫' },
  'default': { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '🔹' }
};

const getGroupConfig = (group) => GROUP_CONFIG[group] || GROUP_CONFIG.default;

/**
 * Advanced Organism Selector with search, filtering, and grouping
 * 
 * @param {Object} props
 * @param {Array} props.organisms - List of organism strings (e.g., "Kiselalger_Skeletonema")
 * @param {string} props.value - Currently selected value
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 */
export default function OrganismSelector({ 
  organisms = [], 
  value, 
  onChange, 
  placeholder = "Velg organisme",
  className = ""
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const searchInputRef = useRef(null);
  
  // Parse organisms into structured data
  const parsedOrganisms = useMemo(() => {
    return organisms.map(org => {
      const parts = org.split('_');
      const group = parts[0] || 'Ukjent';
      const species = parts.slice(1).join(' ') || org;
      return {
        value: org,
        group,
        species,
        displayName: `${group} - ${species}`,
        searchText: `${group} ${species}`.toLowerCase()
      };
    });
  }, [organisms]);
  
  // Get unique groups
  const groups = useMemo(() => {
    const groupSet = new Set(parsedOrganisms.map(o => o.group));
    return Array.from(groupSet).sort();
  }, [parsedOrganisms]);
  
  // Filter organisms based on search and group filter
  const filteredOrganisms = useMemo(() => {
    let filtered = parsedOrganisms;
    
    // Filter by selected group
    if (selectedGroup) {
      filtered = filtered.filter(o => o.group === selectedGroup);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(o => o.searchText.includes(query));
    }
    
    return filtered;
  }, [parsedOrganisms, selectedGroup, searchQuery]);
  
  // Group filtered organisms by their group
  const groupedOrganisms = useMemo(() => {
    const grouped = {};
    filteredOrganisms.forEach(org => {
      if (!grouped[org.group]) {
        grouped[org.group] = [];
      }
      grouped[org.group].push(org);
    });
    return grouped;
  }, [filteredOrganisms]);
  
  // Get display text for selected value
  const selectedDisplay = useMemo(() => {
    if (!value || value === 'all') return null;
    const found = parsedOrganisms.find(o => o.value === value);
    return found ? found : { displayName: value.replace(/_/g, ' '), group: 'Ukjent' };
  }, [value, parsedOrganisms]);
  
  // Focus search input when popover opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);
  
  const handleSelect = (orgValue) => {
    onChange(orgValue);
    setOpen(false);
    setSearchQuery('');
  };
  
  const clearSelection = (e) => {
    e.stopPropagation();
    onChange('all');
  };
  
  const clearGroupFilter = () => {
    setSelectedGroup(null);
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-[280px] justify-between border-slate-300 bg-white shadow-sm hover:bg-slate-50",
            className
          )}
        >
          {selectedDisplay ? (
            <div className="flex items-center gap-2 truncate">
              <span className="text-lg">{getGroupConfig(selectedDisplay.group).icon}</span>
              <span className="truncate">{selectedDisplay.species || selectedDisplay.displayName}</span>
              <Badge variant="outline" className={cn("text-xs ml-1", getGroupConfig(selectedDisplay.group).color)}>
                {selectedDisplay.group}
              </Badge>
            </div>
          ) : (
            <span className="text-slate-500">{placeholder}</span>
          )}
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {selectedDisplay && (
              <X 
                className="h-4 w-4 text-slate-400 hover:text-slate-600" 
                onClick={clearSelection}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 text-slate-400" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <div className="p-3 border-b bg-slate-50">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              ref={searchInputRef}
              placeholder="Søk etter art eller gruppe..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 bg-white"
            />
            {searchQuery && (
              <X 
                className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 cursor-pointer hover:text-slate-600" 
                onClick={() => setSearchQuery('')}
              />
            )}
          </div>
          
          {/* Group filter pills */}
          <div className="mt-3">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="h-3 w-3 text-slate-500" />
              <span className="text-xs font-medium text-slate-600">Filtrer på gruppe:</span>
              {selectedGroup && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-5 text-xs text-slate-500 hover:text-slate-700 px-1"
                  onClick={clearGroupFilter}
                >
                  Nullstill
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {groups.map(group => {
                const config = getGroupConfig(group);
                const isSelected = selectedGroup === group;
                const count = parsedOrganisms.filter(o => o.group === group).length;
                
                return (
                  <button
                    key={group}
                    onClick={() => setSelectedGroup(isSelected ? null : group)}
                    className={cn(
                      "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-all",
                      isSelected 
                        ? `${config.color} ring-2 ring-offset-1 ring-slate-400` 
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    )}
                  >
                    <span>{config.icon}</span>
                    <span>{group}</span>
                    <span className="text-[10px] opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Results */}
        <div className="max-h-[300px] overflow-y-auto">
          {/* "All" option */}
          <button
            onClick={() => handleSelect('all')}
            className={cn(
              "w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors border-b",
              value === 'all' && "bg-blue-50"
            )}
          >
            <Microscope className="h-4 w-4 text-slate-500" />
            <span className="font-medium">Alle organismer</span>
            <span className="text-xs text-slate-500 ml-auto">({parsedOrganisms.length} totalt)</span>
            {value === 'all' && <Check className="h-4 w-4 text-blue-600 ml-2" />}
          </button>
          
          {/* Grouped organisms */}
          {Object.keys(groupedOrganisms).length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p>Ingen treff for &ldquo;{searchQuery}&rdquo;</p>
              <button 
                className="text-blue-600 text-sm mt-2 hover:underline"
                onClick={() => { setSearchQuery(''); setSelectedGroup(null); }}
              >
                Nullstill søk
              </button>
            </div>
          ) : (
            Object.entries(groupedOrganisms).map(([group, orgs]) => {
              const config = getGroupConfig(group);
              return (
                <div key={group}>
                  {/* Group header */}
                  <div className={cn(
                    "sticky top-0 px-4 py-2 text-xs font-semibold flex items-center gap-2 border-b",
                    config.color
                  )}>
                    <span>{config.icon}</span>
                    <span>{group}</span>
                    <span className="opacity-70">({orgs.length})</span>
                  </div>
                  
                  {/* Organisms in group */}
                  {orgs.map(org => (
                    <button
                      key={org.value}
                      onClick={() => handleSelect(org.value)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-slate-50 transition-colors",
                        value === org.value && "bg-blue-50"
                      )}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-300"></div>
                      <span className="flex-1">{org.species}</span>
                      {value === org.value && <Check className="h-4 w-4 text-blue-600" />}
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
        
        {/* Footer with count */}
        <div className="p-2 border-t bg-slate-50 text-xs text-slate-500 text-center">
          Viser {filteredOrganisms.length} av {parsedOrganisms.length} organismer
        </div>
      </PopoverContent>
    </Popover>
  );
}
